import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MenuItemResponse } from "@/api/lib/db/types";
import Icon from "@/src/lib/components/custom/Icon";
import { Image } from "@/src/lib/components/custom/Image";
import { Badge } from "../../../lib/components/ui/badge";
import { Button } from "../../../lib/components/ui/button";
import { Card } from "../../../lib/components/ui/card";
import { LoginRequiredDialog } from "../../../lib/components/ui/login-required-dialog";
import { useApi } from "../../../lib/hooks/api/use-api";
import ItemDialog from "./ItemDialog";

type Props = {
	item: MenuItemResponse;
};

export default function ItemCard({ item }: Props) {
	const { t } = useTranslation();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [loginDialogOpen, setLoginDialogOpen] = useState(false);
	const { data: session } = useApi().auth.useSession();

	const rating = Math.max(0, Math.min(5, item.rating));

	// Fetch all menu sizes and filter for this item
	const { data: allSizes } = useApi().menu.getSizes();

	// Filter sizes for this menu item and get the lowest price
	const itemSizes =
		allSizes?.filter(
			(size) => size.menuItemId === item.id && size.isAvailable,
		) || [];
	const lowestPrice =
		itemSizes.length > 0
			? Math.min(...itemSizes.map((size) => parseFloat(size.price)))
			: null;

	const handleAddClick = () => {
		if (!session?.user) {
			setLoginDialogOpen(true);
			return;
		}
		setDialogOpen(true);
	};

	return (
		<>
			<Card className="relative overflow-visible rounded-2xl p-6 bg-card flex flex-col h-full">
				{/* Content that grows */}
				<div className="flex-1 flex flex-col gap-4 mb-4">
					<div className="absolute -top-18 right-10 justify-center pointer-events-none hidden md:flex">
						<div className="size-36 md:size-40 rounded-full bg-background shadow-md flex items-center justify-center">
							{item.imageUrl ? (
								<Image
									src={item.imageUrl}
									alt={item.name}
									className="size-32 md:size-36 rounded-full object-cover"
								/>
							) : (
								<div className="size-32 md:size-36 rounded-full bg-accent" />
							)}
						</div>
					</div>

					{/* Dietary badges */}

					<div className="flex flex-col gap-2">
						<div className="flex items-center gap-4">
							<Image
								src={item.imageUrl}
								alt={item.name}
								className="w-20 md:hidden aspect-square rounded-full object-cover"
							/>

							<div>
								<h4 className="font-semibold text-lg xl:max-w-32">
									{item.name}
								</h4>
								{item.description && (
									<p className="text-sm md:hidden text-muted-foreground">
										{item.description}
									</p>
								)}

								<div className="flex items-center gap-2 mt-0 md:mt-1">
									<div className="text-primary text-sm">
										{Array.from({ length: 5 }).map((_, i) => (
											<span key={`${item.name}-rating-${i}`}>
												{i < rating ? "★" : "☆"}
											</span>
										))}
									</div>
									<span className="text-xs text-muted-foreground">
										({item.rating.toFixed(1)})
									</span>
								</div>
							</div>
						</div>

						{item.description && (
							<p className="text-sm hidden md:block text-muted-foreground mt-2">
								{item.description}
							</p>
						)}

						<div className="flex items-start md:items-center gap-2 mt-2">
							<Badge className="">{item.category}</Badge>

							{item.isVegan ? (
								<Badge
									variant="secondary"
									className="text-xs bg-green-100 text-green-700 border-green-200"
								>
									🌱 {t("common.vegan")}
								</Badge>
							) : item.isVegetarian ? (
								<Badge
									variant="secondary"
									className="text-xs bg-blue-100 text-blue-700 border-blue-200"
								>
									🥬 {t("common.vegetarian")}
								</Badge>
							) : null}
						</div>
					</div>
				</div>

				{/* Price and Add to Cart Button Row - Fixed at bottom */}
				<div className="flex flex-col gap-4">
					<div className="flex items-center justify-between">
						{lowestPrice && (
							<div className="text-2xl flex flex-col gap-4 font-bold text-primary">
								€{lowestPrice.toFixed(2)}
							</div>
						)}

						<Button
							variant="primary"
							className="rounded-full"
							onClick={handleAddClick}
							aria-label={`Add ${item.name} to cart`}
							disabled={item.isAvailable === false}
						>
							<Icon name="Plus" />
							{t("home.item_card.add_to_cart")}
						</Button>
					</div>
				</div>
			</Card>

			<ItemDialog
				item={item}
				sizes={itemSizes}
				open={dialogOpen}
				onOpenChange={setDialogOpen}
			/>

			<LoginRequiredDialog
				open={loginDialogOpen}
				onOpenChange={setLoginDialogOpen}
			/>
		</>
	);
}
